import {setCityDirection} from '../i18n';
import { cityLocale } from "../i18n";
import { cityPath, sitePath } from "../urls";
import { CityApi } from "../api/client";
import type {
  AssetOffer,
  CityAsset,
  CityRuntimeResponse,
  PlotData,
  WalletResponse,
} from "../api/types";

type EconomyTab = "assets" | "wallet" | "properties";

export class EconomyCenter {
  private readonly root = document.createElement("div");
  private readonly content = document.createElement("div");
  private readonly notice = document.createElement("div");
  private readonly tabs = new Map<EconomyTab, HTMLButtonElement>();
  private currentTab: EconomyTab = "assets";
  private offers: AssetOffer[] | null = null;
  private walletData: WalletResponse | null = null;
  private renderVersion = 0;
  private readonly balanceSummary = document.createElement('span');

  constructor(
    private readonly api: CityApi,
    private readonly runtime: CityRuntimeResponse,
    private readonly onDesignPlot: (plot: PlotData) => void,
    private readonly onPurchasePlot?: (plot: PlotData) => Promise<void>,
    private readonly onBalanceChanged: (balance: string) => void = () => {},
  ) {
    this.root.className = "city-modal city-economy";setCityDirection(this.root);
    this.root.hidden = true;

    const panel = document.createElement("section");
    panel.className = "city-modal__panel city-economy__panel";
    panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-modal", "true");

    const header = document.createElement("header");
    header.className = "city-modal__header";
    const heading = document.createElement("div");
    heading.innerHTML =
      "<strong>مرکز خرید شهر</strong><small>زمین، اَسِت و کیف پول</small>";
    const close = document.createElement("button");
    close.type = "button";
    close.className = "city-modal__close";
    close.textContent = "×";
    close.addEventListener("click", () => this.close());
    header.append(heading, close);

    const summary = document.createElement("div");
    summary.className = "city-economy__summary";
    const balance = this.balanceSummary;
    balance.innerHTML = `موجودی <strong>${Number(
      runtime.viewer.walletBalance || 0,
    ).toLocaleString(cityLocale())} ${runtime.release.currencyName || "KSC"}</strong>`;
    summary.appendChild(balance);
    if (!runtime.viewer.authenticated) {
      const login = document.createElement("a");
      login.href = `${sitePath("customer/login/")}?next=${encodeURIComponent(cityPath())}`;
      login.textContent = "ورود برای خرید و ساخت مغازه";
      summary.appendChild(login);
    }

    const nav = document.createElement("nav");
    nav.className = "city-modal__tabs";
    const definitions: Array<[EconomyTab, string]> = [
      ["assets", "فروشگاه اَسِت"],
      ["wallet", "کیف پول"],
      ["properties", "املاک من"],
    ];
    definitions.forEach(([tab, label], index) => {
      const button = document.createElement("button");
      button.type = "button";
      button.textContent = label;
      if (index === 0) button.classList.add("is-active");
      button.addEventListener("click", () => void this.open(tab));
      this.tabs.set(tab, button);
      nav.appendChild(button);
    });

    this.notice.className = "city-modal__notice";
    this.notice.hidden = true;
    this.content.className = "city-modal__content";

    panel.append(header, summary, nav, this.notice, this.content);
    this.root.appendChild(panel);
    document.body.appendChild(this.root);

    this.root.addEventListener("pointerdown", (event) => {
      if (event.target === this.root) this.close();
    });
  }

  async open(tab: EconomyTab = "assets"): Promise<void> {
    const version = ++this.renderVersion;
    this.currentTab = tab;
    this.root.dataset.tab = tab;
    const heading = this.root.querySelector<HTMLElement>(".city-modal__header strong");
    const subtitle = this.root.querySelector<HTMLElement>(".city-modal__header small");
    if (heading) heading.textContent = tab === "properties" ? "املاک من" : "مرکز خرید شهر";
    if (subtitle) subtitle.textContent = tab === "properties"
      ? "مدیریت زمین‌های متعلق به شما" : "زمین، اَسِت و کیف پول";
    this.root.hidden = false;
    document.body.classList.add("city-modal-open");
    this.tabs.forEach((button, key) => {
      button.classList.toggle("is-active", key === tab);
    });
    this.notice.hidden = true;

    if (tab === "properties") {
      this.renderLand(this.runtime.plots.filter((plot) => plot.ownedByViewer), true);
    }
    if (tab === "assets") await this.renderAssets(version);
    if (tab === "wallet") await this.renderWallet(version);
  }

  close(): void {
    this.renderVersion++;
    this.root.hidden = true;
    document.body.classList.remove("city-modal-open");
  }

  destroy(): void {
    this.root.remove();
  }

  private showNotice(message: string, error = false): void {
    this.notice.hidden = false;
    this.notice.className = error
      ? "city-modal__notice is-error"
      : "city-modal__notice";
    this.notice.textContent = message;
  }

  private renderLand(plots: PlotData[], propertiesOnly = false): void {
    this.content.replaceChildren();
    const grid = document.createElement("div");
    grid.className = "city-card-grid";

    if (!plots.length) {
      const empty = document.createElement("p");
      empty.className = "city-empty";
      empty.textContent = propertiesOnly
        ? "هنوز زمینی در این شهر ندارید."
        : "زمینی برای نمایش وجود ندارد.";
      this.content.appendChild(empty);
      return;
    }

    for (const plot of plots) {
      const card = document.createElement("article");
      card.className = "city-card";
      if (plot.ownedByViewer) card.classList.add("is-owned");

      const title = document.createElement("strong");
      title.textContent = plot.name;
      title.dir = "auto";
      const meta = document.createElement("small");
      meta.textContent = [
        plot.district || "شهر",
        `${plot.width}×${plot.depth}`,
        `مختصات ${plot.gridX}، ${plot.gridY}`,
      ].join(" · ");

      const status = document.createElement("b");
      status.className = "city-card__price";
      status.textContent = plot.ownedByViewer
        ? "ملک شما"
        : plot.forSale
          ? `${Number(plot.price).toLocaleString(cityLocale())} ${this.runtime.release.currencyName}`
          : plot.listing?.isListed
            ? `${Number(plot.listing.buyerTotal).toLocaleString(cityLocale())} ${this.runtime.release.currencyName}`
            : plot.reserved
              ? "رزرو شده"
              : "فروخته شده";

      const actions = document.createElement("div");
      actions.className = "city-card__actions";

      if (plot.forSale && this.runtime.release.landPurchase) {
        const buy = document.createElement("button");
        buy.type = "button";
        buy.className = "city-btn city-btn--primary";
        buy.textContent = "خرید زمین";
        buy.disabled = !this.runtime.viewer.isCustomer;
        buy.addEventListener("click", () => void this.buyPlot(plot, buy));
        actions.appendChild(buy);
      }

      if (
        plot.listing?.isListed &&
        !plot.listing.isViewerSeller &&
        this.runtime.viewer.isCustomer
      ) {
        const buyListing = document.createElement("button");
        buyListing.type = "button";
        buyListing.className = "city-btn city-btn--primary";
        buyListing.textContent = "خرید از مالک";
        buyListing.addEventListener("click", () =>
          void this.buyListing(plot, buyListing));
        actions.appendChild(buyListing);
      }

      if (plot.ownedByViewer) {
        const design = document.createElement("button");
        design.type = "button";
        design.className = "city-btn city-btn--primary";
        design.textContent = plot.storeId ? "طراحی / مدیریت مغازه" : "ساخت مغازه";
        design.addEventListener("click", () => {
          this.close();
          this.onDesignPlot(plot);
        });
        actions.appendChild(design);

        if (this.runtime.release.landResale) {
          actions.appendChild(this.listingEditor(plot));
        }
      }

      card.append(title, meta, status, actions);
      grid.appendChild(card);
    }

    this.content.appendChild(grid);
  }

  /** Listing price is entered in a modal, then reviewed before any write. */
  private listingEditor(plot: PlotData): HTMLElement {
    const actions = document.createElement("div");
    actions.className = "city-listing-actions";
    const open = document.createElement("button");
    open.type = "button";
    open.className = "city-btn city-btn--resale";
    open.textContent = plot.listing?.isListed ? "ویرایش قیمت فروش" : "فروش زمین";
    open.addEventListener("click", () => this.openListingDialog(plot));
    actions.append(open);
    if (plot.listing?.isListed) {
      const cancel = document.createElement("button");
      cancel.type = "button";
      cancel.className = "city-btn city-btn--danger";
      cancel.textContent = "لغو آگهی";
      cancel.addEventListener("click", async () => {
        if (!window.confirm("آگهی فروش این زمین لغو شود؟")) return;
        cancel.disabled = true;
        try {
          await this.api.cancelListing(plot.id);
          location.reload();
        } catch (error) {
          cancel.disabled = false;
          this.showNotice(
            error instanceof Error ? error.message : "لغو آگهی ناموفق بود.",
            true,
          );
        }
      });
      actions.append(cancel);
    }
    return actions;
  }

  private openListingDialog(plot: PlotData): void {
    const dialog = document.createElement("dialog");
    dialog.className = "city-resale-dialog";
    setCityDirection(dialog);
    dialog.setAttribute("aria-labelledby", "city-resale-title");
    dialog.setAttribute("aria-describedby", "city-resale-help");
    const form = document.createElement("form");
    form.className = "city-resale-form";
    form.noValidate = true;

    const title = document.createElement("h2");
    title.id = "city-resale-title";
    title.textContent = plot.listing?.isListed ? "ویرایش آگهی فروش" : "فروش زمین";
    const propertyName = document.createElement("p");
    propertyName.className = "city-resale-property";
    propertyName.textContent = plot.name;
    propertyName.dir = "auto";
    const help = document.createElement("p");
    help.id = "city-resale-help";
    help.textContent = "قیمت پیشنهادی فروش زمین را وارد کنید.";
    const label = document.createElement("label");
    label.htmlFor = "city-resale-price";
    label.textContent = `قیمت بازفروش (${this.runtime.release.currencyName || "KSC"})`;
    const input = document.createElement("input");
    input.id = "city-resale-price";
    input.name = "askingPrice";
    input.type = "number";
    input.min = "1";
    input.step = "1";
    input.required = true;
    input.inputMode = "numeric";
    input.placeholder = "مبلغ فروش";
    input.value = plot.listing?.askingPrice ?? "";
    const errorText = document.createElement("p");
    errorText.className = "city-resale-error";
    errorText.setAttribute("role", "alert");
    errorText.hidden = true;
    const review = document.createElement("div");
    review.className = "city-resale-review";
    review.hidden = true;
    const reviewTitle = document.createElement("strong");
    reviewTitle.textContent = "تأیید قیمت فروش";
    const reviewAmount = document.createElement("p");
    review.append(reviewTitle, reviewAmount);
    const buttons = document.createElement("div");
    buttons.className = "city-resale-buttons";
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "city-btn";
    cancel.textContent = "انصراف";
    const next = document.createElement("button");
    next.type = "submit";
    next.className = "city-btn city-btn--primary";
    next.textContent = "بررسی و تأیید";
    buttons.append(cancel, next);
    form.append(title, propertyName, help, label, input, review, errorText, buttons);
    dialog.append(form);
    document.body.append(dialog);
    let confirming = false;
    let saving = false;
    let confirmedPrice = "";
    const close = () => {
      if (!saving && dialog.open) dialog.close();
    };
    cancel.addEventListener("click", () => {
      if (confirming) {
        confirming = false;
        review.hidden = true;
        input.hidden = false;
        label.hidden = false;
        help.hidden = false;
        cancel.textContent = "انصراف";
        next.textContent = "بررسی و تأیید";
        errorText.hidden = true;
        input.focus();
      } else close();
    });
    dialog.addEventListener("cancel", event => {
      if (saving) event.preventDefault();
    });
    dialog.addEventListener("close", () => dialog.remove(), { once: true });
    form.addEventListener("submit", async event => {
      event.preventDefault();
      if (saving) return;
      errorText.hidden = true;
      if (!confirming) {
        const price = input.value.trim();
        const numeric = Number(price);
        if (!/^\d+$/.test(price) || !Number.isSafeInteger(numeric) || numeric <= 0) {
          errorText.textContent = "یک قیمت معتبر و بزرگ‌تر از صفر وارد کنید.";
          errorText.hidden = false;
          input.focus();
          return;
        }
        confirmedPrice = price;
        reviewAmount.textContent = `قیمت «${plot.name}»: ${numeric.toLocaleString(cityLocale())} ${this.runtime.release.currencyName || "KSC"}`;
        confirming = true;
        input.hidden = true;
        label.hidden = true;
        help.hidden = true;
        review.hidden = false;
        cancel.textContent = "ویرایش قیمت";
        next.textContent = plot.listing?.isListed ? "تأیید و ویرایش آگهی" : "تأیید و ثبت آگهی";
        next.focus();
        return;
      }
      saving = true;
      next.disabled = true;
      cancel.disabled = true;
      next.textContent = "در حال ثبت…";
      try {
        if (plot.listing?.isListed) await this.api.updateListing(plot.id, confirmedPrice);
        else await this.api.createListing(plot.id, confirmedPrice);
        dialog.close();
        location.reload();
      } catch (error) {
        saving = false;
        next.disabled = false;
        cancel.disabled = false;
        next.textContent = plot.listing?.isListed ? "تأیید و ویرایش آگهی" : "تأیید و ثبت آگهی";
        errorText.textContent = error instanceof Error ? error.message : "ثبت آگهی ناموفق بود.";
        errorText.hidden = false;
      }
    });
    dialog.showModal();
    input.focus();
  }

  private async buyPlot(plot: PlotData, button: HTMLButtonElement): Promise<void> {
    if (this.onPurchasePlot) {
      button.disabled = true;
      try { await this.onPurchasePlot(plot); }
      finally { button.disabled = false; }
      return;
    }
    if (!this.runtime.viewer.isCustomer) {
      location.href = `${sitePath("customer/login/")}?next=${encodeURIComponent(cityPath())}`;
      return;
    }
    if (!confirm(`خرید «${plot.name}»؟`)) return;
    button.disabled = true;
    button.textContent = "در حال خرید…";
    try {
      await this.api.purchasePlot(plot.id);
      location.reload();
    } catch (error) {
      button.disabled = false;
      button.textContent = "خرید زمین";
      this.showNotice(
        error instanceof Error ? error.message : "خرید زمین ناموفق بود.",
        true,
      );
    }
  }

  private async buyListing(
    plot: PlotData,
    button: HTMLButtonElement,
  ): Promise<void> {
    const listingId = plot.listing?.listingId;
    if (!listingId) return;
    if (!confirm("خرید این ملک از مالک فعلی؟")) return;
    button.disabled = true;
    try {
      await this.api.purchaseListing(listingId);
      location.reload();
    } catch (error) {
      button.disabled = false;
      this.showNotice(
        error instanceof Error ? error.message : "خرید ملک ناموفق بود.",
        true,
      );
    }
  }

  private async renderAssets(version = this.renderVersion): Promise<void> {
    this.content.innerHTML = '<p class="city-empty">در حال بارگذاری فروشگاه اَسِت…</p>';
    try {
      this.offers ??= (await this.api.assetStore()).offers;
      if (version !== this.renderVersion) return;
      const grid = document.createElement("div");
      grid.className = "city-card-grid city-asset-grid";
      const assetMap = new Map(
        this.runtime.assets.map((asset) => [asset.assetId, asset]),
      );

      for (const offer of this.offers) {
        const card = document.createElement("article");
        card.className = "city-card city-asset-card";
        if (offer.featured) card.classList.add("is-featured");

        const asset = assetMap.get(offer.assetId);
        const preview = document.createElement("div");
        preview.className = "city-asset-card__preview";
        if (offer.previewUrl && !asset?.sourceRect) {
          const img = document.createElement("img");
          img.src = offer.previewUrl;
          img.alt = "";
          preview.appendChild(img);
        } else {
          preview.textContent = (offer.name || "A").slice(0, 2).toUpperCase();
        }

        const title = document.createElement("strong");
        title.textContent = offer.name;
        const meta = document.createElement("small");
        meta.textContent = offer.category || "asset";
        const price = document.createElement("b");
        price.className = "city-card__price";
        price.textContent = offer.isFree
          ? "رایگان"
          : `${Number(offer.price).toLocaleString(cityLocale())} ${this.runtime.release.currencyName}`;

        const action = document.createElement("button");
        action.type = "button";
        action.className = "city-btn city-btn--primary";
        action.disabled = !this.runtime.viewer.isCustomer ||
          (Boolean(offer.purchaseLimit) &&
            offer.ownedQuantity >= Number(offer.purchaseLimit));
        action.textContent = offer.ownedQuantity > 0
          ? `موجودی × ${offer.ownedQuantity}`
          : offer.isFree ? "دریافت" : "خرید اَسِت";
        action.addEventListener("click", () => void this.buyAsset(offer, action));

        card.append(preview, title, meta, price, action);
        grid.appendChild(card);
      }
      this.content.replaceChildren(grid);
    } catch (error) {
      if (version !== this.renderVersion) return;
      this.content.replaceChildren();
      this.showNotice(
        error instanceof Error ? error.message : "بارگذاری اَسِت‌ها ناموفق بود.",
        true,
      );
    }
  }

  private async buyAsset(
    offer: AssetOffer,
    button: HTMLButtonElement,
  ): Promise<void> {
    if (!this.runtime.viewer.isCustomer) {
      location.href = `${sitePath("customer/login/")}?next=${encodeURIComponent(cityPath())}`;
      return;
    }
    button.disabled = true;
    const version = this.renderVersion;
    button.textContent = "در حال انجام…";
    try {
      const result = await this.api.purchaseAsset(offer.id);
      this.updateBalance(result.walletBalance);
      offer.ownedQuantity = result.ownedQuantity;
      this.offers = null;
      if (version !== this.renderVersion) return;
      await this.renderAssets(version);
      if (version !== this.renderVersion) return;
      this.showNotice("اَسِت به موجودی شما اضافه شد.");
    } catch (error) {
      button.disabled = false;
      button.textContent = offer.isFree ? "دریافت" : "خرید اَسِت";
      if (version !== this.renderVersion) return;
      this.showNotice(
        error instanceof Error ? error.message : "خرید اَسِت ناموفق بود.",
        true,
      );
    }
  }

  private updateBalance(balance: string): void {
    this.runtime.viewer.walletBalance = balance;
    this.balanceSummary.textContent = `موجودی ${Number(balance).toLocaleString(cityLocale())} ${this.runtime.release.currencyName || 'KSC'}`;
    this.onBalanceChanged(balance);
  }

  private async renderWallet(version = this.renderVersion): Promise<void> {
    this.content.innerHTML = '<p class="city-empty">در حال بارگذاری کیف پول…</p>';
    if (!this.runtime.viewer.isCustomer) {
      this.content.innerHTML =
        '<p class="city-empty">برای مشاهده کیف پول وارد حساب مشتری شوید.</p>';
      return;
    }

    try {
      const walletData = await this.api.wallet();
      if (version !== this.renderVersion) return;
      this.walletData = walletData;
      this.updateBalance(walletData.balance);
      const wrap = document.createElement("div");
      wrap.className = "city-wallet-panel";

      const balance = document.createElement("div");
      balance.className = "city-wallet-panel__balance";
      balance.innerHTML = `<span>موجودی فعلی</span><strong>${Number(
        this.walletData.balance,
      ).toLocaleString(cityLocale())} ${this.runtime.release.currencyName}</strong>`;
      wrap.appendChild(balance);

      const list = document.createElement("div");
      list.className = "city-wallet-transactions";
      for (const entry of this.walletData.transactions) {
        const row = document.createElement("div");
        row.className = "city-wallet-transaction";
        const amount = document.createElement("b");
        amount.textContent = `${entry.kind === "credit" ? "+" : "−"}${Number(
          entry.amount,
        ).toLocaleString(cityLocale())}`;
        const reason = document.createElement("span");
        reason.textContent = entry.reason.replaceAll("_", " ");
        const date = document.createElement("time");
        date.textContent = new Date(entry.createdAt).toLocaleDateString("fa-IR");
        row.append(amount, reason, date);
        list.appendChild(row);
      }
      if (!this.walletData.transactions.length) {
        list.innerHTML = '<p class="city-empty">هنوز تراکنشی ثبت نشده است.</p>';
      }
      wrap.appendChild(list);
      this.content.replaceChildren(wrap);
    } catch (error) {
      if (version !== this.renderVersion) return;
      this.showNotice(
        error instanceof Error ? error.message : "بارگذاری کیف پول ناموفق بود.",
        true,
      );
    }
  }
}
